/**
 * @typedef {Object} SpecialtyProps
 * @property {boolean} isRegistrableFlag
 * @property {string} productUrl
 * @returns {boolean}
 */
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { usePageData } from '@composables/usePageData';

import { useKillswitches, useProductFlags, useFeatureEligibility } from '@modules/featureControl';

/**
 * checks if the user is on a registry
 * @param {SpecialtyProps} param0
 * @returns {boolean}
 */
const validateRegistry = ({ isRegistrableFlag, productUrl = '' }) => isRegistrableFlag && productUrl?.toLowerCase()?.indexOf('registry/wedding') > -1;

/**
 * salesfloor data
 *
 * @returns {{
 * salesFloorProps: {"teleport-disabled": boolean, "widget-type": string, "target-element": string, "widget-name": string, "is-registry"},
 * salesFloorClasses: {salesfloor: (*|boolean)},
 * showSalesFloorWidget: (*|boolean)
 * }}
 */
export function useSalesFloorData() {
  const { killswitches } = useKillswitches();
  const { isSalesFloorEnabled, isSalesFloorVisible } = useFeatureEligibility();
  const { isRegistrableFlag } = useProductFlags();

  const { categoryId, productId } = usePageData();
  const { topLevelCategoryID, productUrl } = useCommonProductGetters({ productId });

  /**
   * TODO -> move data to object and select using widget name
   * returns contextual widget props data
   * @returns {getContextualWidgetProps}
   */
  const _getContextualWidgetProps = () => {
    if (killswitches.isInPageSfContextualWidgetEnabled) {
      return { widgetName: 'virtualAdvisorWidget', teleportDisabled: true };
    }
    if (killswitches.contextualWidgetEnabled) {
      return { widgetName: 'floatingWidget' };
    }
    return {};
  };

  /**
   * returns specialty props data
   * uses product data
   * @returns {SpecialtyProps}
   */
  const _getSpecialtyPropsData = () => ({
    isRegistry: validateRegistry({ isRegistrableFlag, productUrl }),
    categoryData: {
      categoryID: `${categoryId}`,
      topLevelCategoryID,
    },
  });

  const showSalesFloorWidget = isSalesFloorEnabled && isSalesFloorVisible;

  // contextual widget props
  const { widgetName = '', targetElement = 'body', teleportDisabled = false } = _getContextualWidgetProps();
  // product data -> specialty data props
  const { isRegistry, categoryData } = _getSpecialtyPropsData();

  const salesFloorProps = {
    'widget-type': 'contextualWidget',
    'widget-name': widgetName,
    'target-element': targetElement,
    'teleport-disabled': teleportDisabled,
    'is-registry': isRegistry,
    ...(!isRegistry && { 'specialty-data': categoryData }),
  };

  return {
    salesFloorProps,
    showSalesFloorWidget,
    salesFloorClasses: {},
  };
}

export default useSalesFloorData;
